import { email } from '@sideway/address'
import { format } from 'date-fns'

import { ScriptDocType } from '@showrunner/codex'

import { NavLinkData } from './constants'

export { v4 as uuid } from 'uuid'

export * from './buildScrapiClient'
export * from './constants'
export * from './fileDownload'
export * from './fireAndForget'
export * as formUtil from './formUtil'
export * from './getAvatarUrl'
export * from './math'
export * from './mixpanel'
export * from './parseServerError'
export * from './pathConfigs'
export * from './pathUtil'
export * from './prosemirrorHelpers'
export * from './safeParseJSON'
export * from './ScriptoApiClient'
export * from './scripts'
export * as styleHelpers from './styleHelpers'

export const noop = () => {
  // do nothing
}

export const noopAsync = () => Promise.resolve()

export const delay = (seconds: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000)
  })

export const toLowerCaseRoman = (num: number): string => {
  const romanNumerals: [number, string][] = [
    [1000, 'm'],
    [900, 'cm'],
    [500, 'd'],
    [400, 'cd'],
    [100, 'c'],
    [90, 'xc'],
    [50, 'l'],
    [40, 'xl'],
    [10, 'x'],
    [9, 'ix'],
    [5, 'v'],
    [4, 'iv'],
    [1, 'i'],
  ]

  let result = ''
  for (const [value, numeral] of romanNumerals) {
    while (num >= value) {
      result += numeral
      num -= value
    }
  }
  return result
}

// call to reload window with a dummy delay at the end. This lets us
// prevent some unwanted UI from rendering after we trigger a reload
export const reloadWindow = async (path?: string) => {
  if (path) {
    window.history.pushState({}, '', path)
  }
  window.location.reload()
  await delay(2)
}

export const stringMapEqual = (a: StringMap, b: StringMap): boolean => {
  if (a === b) {
    return true
  }
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length) {
    return false
  }
  return !keys.find((key) => a[key] !== b[key])
}

export const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()

export const isEmailValid = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return email.isValid(value)
  }
  return false
}

export const pluralize = (count: number, text: string) =>
  count === 1 ? text : text + 's'

export const formatMoney = (
  minorUnits: number,
  currency: string,
  options?: Intl.NumberFormatOptions,
): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    ...options,
  })
  // Stripe amounts are in the smallest currency unit (cents for USD, yen for JPY)
  const { maximumFractionDigits = 2 } = formatter.resolvedOptions()
  return formatter.format(minorUnits / 10 ** maximumFractionDigits)
}

export const createMailtoUrl = (
  email: string,
  { subject }: { subject: string },
) => `mailto:${email}?subject=${encodeURIComponent(subject)}`

export const getPrintTimestamp = () => {
  return format(new Date(), "M/d/yy 'at' h:mm aa")
}

export const getPrintDate = () => {
  return format(new Date(), 'M/d/yy')
}

export function isInteger(val: unknown): val is number {
  return Number.isInteger(val)
}

// handy little generic typeguard... if you have an array of thigs that are a mix of
// T and null/undefined you can use this filter to typecast it to an array of T
// see https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
export function notEmptyFilter<TValue>(
  value: TValue | null | undefined,
): value is TValue {
  return value !== null && value !== undefined
}

// for turning stuff like "&apos;" into "'"
// (because for some reason we encode comment text)
export const decodeHtmlString = (input?: string | null): string => {
  if (!input) {
    return ''
  }
  const doc = new DOMParser().parseFromString(input, 'text/html')
  return doc.documentElement.textContent ?? ''
}

export const navLinkText = (link: NavLinkData) => {
  const { elementNumber, text } = link
  const prefix =
    elementNumber && elementNumber.length > 0 ? `${elementNumber}. ` : ''
  return prefix + text
}

export type SleneType = 'slug' | 'scene'

export const sleneTypeForDoc = (docType: ScriptDocType): SleneType =>
  docType === 'screenplay' ? 'scene' : 'slug'
